import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Plane, Train, Car, Hotel, MapPin, Navigation as NavigationIcon, Users, CheckCircle, Droplet, Battery, MessageCircle, Calendar, Award, HelpCircle, Accessibility, ParkingCircle, Bus } from 'lucide-react';
import Navigation from '../components/Navigation';

const HotelTravelPage = () => {
  const hotels = [
    {
      name: 'Grand Plaza Hotel',
      description: 'Premium accommodations with conference shuttle service',
      distance: '0.3 miles from venue',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&h=400&fit=crop'
    },
    {
      name: 'Downtown Suites',
      description: 'Modern suites perfect for groups and families',
      distance: '0.5 miles from venue',
      image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&h=400&fit=crop'
    },
    {
      name: 'City Center Inn',
      description: 'Budget-friendly rooms with great amenities',
      distance: '0.8 miles from venue',
      image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&h=400&fit=crop'
    },
    {
      name: 'Convention District Hotel',
      description: 'Walking distance to all conference activities',
      distance: '0.2 miles from venue',
      image: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=600&h=400&fit=crop'
    }
  ];

  const travelTips = [
    {
      icon: CheckCircle,
      title: 'Pack for Purpose',
      description: 'Comfortable shoes, conference badge, notebook, faith journal, and business cards.'
    },
    {
      icon: Droplet,
      title: 'Stay Hydrated & Rested',
      description: 'Multi-day events require energy—drink water and get rest each night.'
    },
    {
      icon: Battery,
      title: 'Charge & Connect',
      description: 'Keep devices charged, download the app, and save your digital ticket.'
    }
  ];

  const quickLinks = [
    { icon: Calendar, title: 'Attendee Guide', href: '/attendee-guide' },
    { icon: Accessibility, title: 'ADA Assistance', href: '#ada' },
    { icon: HelpCircle, title: 'FAQs', href: '#faq' },
    { icon: Award, title: 'Badge Activation', href: '#badge' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Header */}
      <div className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1920&h=1080&fit=crop')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B]/80 via-[#0B1B3B]/70 to-[#CFA64B]/60" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          {/* Breadcrumb */}
          <div className="flex items-center justify-center gap-2 text-sm mb-6 text-gray-300">
            <a href="/" className="hover:text-[#CFA64B] transition-colors">Home</a>
            <span>/</span>
            <span className="text-gray-300">Conference Info</span>
            <span>/</span>
            <span className="text-white font-medium">Hotel & Travel</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Hotel & <span className="text-[#CFA64B]">Travel</span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-200">
            Your journey to KNOWN CON starts here.
          </p>
        </div>
      </div>

      {/* Quick Info Bar */}
      <div className="bg-gradient-to-r from-[#0B1B3B] to-[#1a2f5a] py-6">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
            <div className="flex flex-col items-center gap-2">
              <Plane className="w-8 h-8 text-[#CFA64B]" />
              <span className="text-sm font-medium">Airport</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Hotel className="w-8 h-8 text-[#CFA64B]" />
              <span className="text-sm font-medium">Hotels</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <ParkingCircle className="w-8 h-8 text-[#CFA64B]" />
              <span className="text-sm font-medium">Parking</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <MapPin className="w-8 h-8 text-[#CFA64B]" />
              <span className="text-sm font-medium">Venue Map</span>
            </div>
          </div>
        </div>
      </div>

      {/* Arriving at KNOWN CON */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-16 text-center">Arriving at KNOWN CON</h2>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            {/* Left: Intro */}
            <div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Getting to KNOWN CON is easy! Located in the heart of Los Angeles, the Convention Center is accessible 
                by air, train, and car. Whether you're flying in from across the country or driving from nearby, 
                we've made your arrival as smooth as possible.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our team has curated the best travel options, hotel partnerships, and local transportation guides 
                to ensure your focus stays on the experience—not the logistics.
              </p>
            </div>

            {/* Right: Travel Methods */}
            <div className="space-y-6">
              <Card className="border-2 border-gray-200 hover:border-[#CFA64B] transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-[#CFA64B]/10 group-hover:bg-[#CFA64B] flex items-center justify-center flex-shrink-0 transition-colors">
                      <Plane className="w-7 h-7 text-[#CFA64B] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">By Air</h3>
                      <p className="text-gray-600 leading-relaxed">
                        LAX (15 miles) and Burbank Airport (12 miles). Shuttle and rideshare available.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200 hover:border-[#CFA64B] transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-[#CFA64B]/10 group-hover:bg-[#CFA64B] flex items-center justify-center flex-shrink-0 transition-colors">
                      <Train className="w-7 h-7 text-[#CFA64B] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">By Train</h3>
                      <p className="text-gray-600 leading-relaxed">
                        Union Station downtown. Metro Red Line connects directly to venue area.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-200 hover:border-[#CFA64B] transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-[#CFA64B]/10 group-hover:bg-[#CFA64B] flex items-center justify-center flex-shrink-0 transition-colors">
                      <Car className="w-7 h-7 text-[#CFA64B] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">By Car</h3>
                      <p className="text-gray-600 leading-relaxed">
                        I-10 and I-110 access. On-site parking $20/day. Valet service available.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Recommended Hotels */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Stay Near the Action</h2>
            <p className="text-xl text-gray-600 mb-6">We've partnered with select hotels offering exclusive attendee rates</p>
            <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-semibold">
              <Hotel className="w-4 h-4 mr-2" />
              Book Your Stay
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {hotels.map((hotel, idx) => (
              <Card key={idx} className="overflow-hidden group hover:shadow-2xl transition-all duration-500 border-2 border-gray-200 hover:border-[#CFA64B]">
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={hotel.image}
                    alt={hotel.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-4 right-4 bg-[#CFA64B] text-[#0B1B3B] px-3 py-1 rounded-full text-xs font-bold">
                    {hotel.distance}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">{hotel.name}</h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{hotel.description}</p>
                  <Button variant="outline" className="w-full border-[#CFA64B] text-[#CFA64B] hover:bg-[#CFA64B] hover:text-[#0B1B3B]">
                    View Details
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Map & Venue Directions */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Find Your Way</h2>
            <p className="text-xl text-gray-600">Los Angeles Convention Center</p>
          </div>

          {/* Map Placeholder */}
          <div className="relative w-full h-96 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl overflow-hidden mb-12 shadow-xl">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <MapPin className="w-16 h-16 text-[#CFA64B] mx-auto mb-4" />
                <p className="text-2xl font-bold text-[#0B1B3B] mb-2">Los Angeles Convention Center</p>
                <p className="text-gray-600">1201 S Figueroa Street, Los Angeles, CA 90015</p>
                <Button className="mt-6 bg-[#0B1B3B] hover:bg-[#1a2f5a] text-white">
                  <NavigationIcon className="w-4 h-4 mr-2" />
                  Open in Google Maps
                </Button>
              </div>
            </div>
          </div>

          {/* Info Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-2 border-gray-200 hover:border-[#CFA64B] transition-all">
              <CardContent className="p-8 text-center">
                <MapPin className="w-12 h-12 text-[#CFA64B] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">Main Venue</h3>
                <p className="text-gray-600 text-sm mb-4">1201 S Figueroa Street<br/>Los Angeles, CA 90015</p>
                <Button variant="outline" size="sm" className="border-[#CFA64B] text-[#CFA64B]">
                  Get Directions
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-[#CFA64B] transition-all">
              <CardContent className="p-8 text-center">
                <ParkingCircle className="w-12 h-12 text-[#CFA64B] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">Parking</h3>
                <p className="text-gray-600 text-sm mb-4">On-site: $20/day<br/>Nearby lots: $15/day</p>
                <Button variant="outline" size="sm" className="border-[#CFA64B] text-[#CFA64B]">
                  View Options
                </Button>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-[#CFA64B] transition-all">
              <CardContent className="p-8 text-center">
                <Accessibility className="w-12 h-12 text-[#CFA64B] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">Accessibility</h3>
                <p className="text-gray-600 text-sm mb-4">Ramps & elevators<br/>Drop-off zones available</p>
                <Button variant="outline" size="sm" className="border-[#CFA64B] text-[#CFA64B]">
                  Learn More
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <Button variant="outline" className="border-[#0B1B3B] text-[#0B1B3B] hover:bg-[#0B1B3B] hover:text-white">
              Download Venue Map PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Transportation Options */}
      <div className="py-20" style={{ backgroundColor: '#F7F9FC' }}>
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Getting Around the City</h2>
            <p className="text-xl text-gray-600">Multiple transportation options for your convenience</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-[#CFA64B] to-[#d97706] rounded-full flex items-center justify-center">
                  <Car className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0B1B3B] mb-4">Ride Share</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Uber, Lyft available. Pickup zones at South Hall entrance.
                </p>
                <div className="text-sm text-gray-500">
                  Estimated cost from LAX: $35-45
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-[#0B1B3B] to-[#1e3a8a] rounded-full flex items-center justify-center">
                  <Bus className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0B1B3B] mb-4">Public Transit</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Metro Red/Purple Lines. Exit at Pico Station.
                </p>
                <div className="text-sm text-gray-500">
                  Day pass: $7 | Weekend: $18
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-[#CFA64B] to-[#d97706] rounded-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0B1B3B] mb-4">Event Shuttle</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Free shuttles from partner hotels. Runs 7 AM - 11 PM.
                </p>
                <div className="text-sm text-gray-500">
                  Every 20 minutes
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Travel Tips */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Before You Travel</h2>
            <p className="text-xl text-gray-600">Essential tips for a smooth conference experience</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {travelTips.map((tip, idx) => {
              const Icon = tip.icon;
              return (
                <Card key={idx} className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 mx-auto mb-6 bg-[#CFA64B]/10 rounded-full flex items-center justify-center">
                      <Icon className="w-8 h-8 text-[#CFA64B]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0B1B3B] mb-3 text-center">{tip.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-center">{tip.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Community Connection Banner */}
      <div className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1920&h=600&fit=crop')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#CFA64B]/90 to-[#d97706]/80" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-[#0B1B3B]">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Share the Journey</h2>
          <p className="text-xl mb-8 leading-relaxed">
            Flying in with friends or your group? Join the community chat to connect before you arrive!
          </p>
          <Button className="bg-[#0B1B3B] hover:bg-[#1a2f5a] text-white font-bold px-10 py-6 text-lg shadow-xl">
            <MessageCircle className="w-5 h-5 mr-2" />
            Join the Group Chat
          </Button>
        </div>
      </div>

      {/* Useful Links */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Useful Travel Resources</h2>
            <p className="text-xl text-gray-600">Everything you need in one place</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {quickLinks.map((link, idx) => {
              const Icon = link.icon;
              return (
                <a
                  key={idx}
                  href={link.href}
                  className="block p-8 bg-white border-2 border-gray-200 hover:border-[#CFA64B] rounded-2xl transition-all duration-300 hover:shadow-xl group"
                >
                  <div className="w-14 h-14 mx-auto mb-4 bg-[#CFA64B]/10 group-hover:bg-[#CFA64B] rounded-full flex items-center justify-center transition-colors">
                    <Icon className="w-7 h-7 text-[#CFA64B] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0B1B3B] text-center group-hover:text-[#CFA64B] transition-colors">
                    {link.title}
                  </h3>
                </a>
              );
            })}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B] via-[#1a2f5a] to-[#CFA64B]" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Plan your journey. Prepare your purpose. KNOWN CON awaits.
          </h2>
          <p className="text-xl mb-10 text-gray-200">
            Your transformative experience is just around the corner
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-[#0B1B3B] hover:bg-gray-100 font-bold px-10 py-6 text-lg">
              Buy Tickets
            </Button>
            <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white/10 border-2 px-10 py-6 text-lg">
              Attendee Guide
            </Button>
            <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white/10 border-2 px-10 py-6 text-lg">
              Experience Overview
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 KNOWN CON. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HotelTravelPage;
